/**
 * TenantBrandingContext
 *
 * Fetches and provides sandbox tenant branding (logo, colors, name) for
 * the per-tenant portal experience at /:tenantSlug/login and /:tenantSlug/portal/*.
 *
 * Usage:
 *   <TenantBrandingProvider slug="acme-capital">
 *     <TenantLogin />
 *   </TenantBrandingProvider>
 *
 *   const { branding, isLoading, notFound } = useTenantBranding();
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export interface TenantBranding {
  slug: string;
  companyName: string;
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  logoUrl: string | null;
  trialActive: boolean;
  trialExpiresAt: string | null;
  wizardCompleted: boolean;
  launched: boolean;
}

interface TenantBrandingContextValue {
  branding: TenantBranding | null;
  isLoading: boolean;
  notFound: boolean;
  refresh: () => void;
}

const TenantBrandingContext = createContext<TenantBrandingContextValue>({
  branding: null,
  isLoading: true,
  notFound: false,
  refresh: () => {},
});

export function useTenantBranding() {
  return useContext(TenantBrandingContext);
}

interface Props {
  slug: string;
  children: ReactNode;
}

export function TenantBrandingProvider({ slug, children }: Props) {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

  async function fetchBranding() {
    setIsLoading(true);
    setNotFound(false);
    try {
      const res = await fetch(`${apiBase}/portal/${encodeURIComponent(slug)}/branding`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success && json.data) {
        setBranding(json.data as TenantBranding);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (slug) fetchBranding();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inject CSS custom properties so child components can use tenant colors
  // without prop-drilling. Applied to a wrapper div in the provider.
  const cssVars = branding
    ? ({
        '--tenant-primary':  branding.primaryColor,
        '--tenant-accent':   branding.accentColor,
        '--tenant-font':     branding.fontFamily,
      } as React.CSSProperties)
    : {};

  return (
    <TenantBrandingContext.Provider value={{ branding, isLoading, notFound, refresh: fetchBranding }}>
      <div style={cssVars}>
        {children}
      </div>
    </TenantBrandingContext.Provider>
  );
}
